// Manifest der Einstellungen (docs/einstellungen-inventar.md).
//
// EINE Datei, die den ganzen Bestand beschreibt -- analog zu routes.ts
// und lib/shell/panels.ts. Ohne sie gibt es weder Suche noch den
// Bereich "Diese Seite": beides braucht eine Liste, die weiss, was es
// ueberhaupt gibt und wohin es gehoert.
//
// Zwei Sorten Eintrag, und der Unterschied ist keine Laune:
//
//   WERT     ein einzelner Wert (Zahl, Text, Schalter, Auswahl). Wird
//            hier gelesen und geschrieben.
//   FLAECHE  eine Liste mit Anlegen, Pruefen, Loeschen (Hosts, SSH,
//            Geheimnisse). Die bleibt eine Seite; der Eintrag verweist
//            nur darauf. Sie in eine Zeile zu pressen hiesse, sie
//            zweimal zu bauen.
//
// Die Zuordnung "gehoert zu welcher Seite" (Feld `seite`) ist die
// Grundlage fuer den Bereich "Diese Seite": liegt das Fenster geteilt
// neben einer Seite, zeigt es deren Einstellungen.
//
// Lesen und Schreiben kapselt der Eintrag selbst. Damit weiss die
// Oberflaeche nichts ueber prefs, Settings-API oder Sonderwege -- sie
// ruft lies() und schreib().

import type { Component } from "svelte";

import { getBgSettings, setBgSettings } from "../bg-settings";
import { AKZENT_STANDARD, FARBE_STANDARD } from "../farbe/farbe";
import { getLang, setLang } from "../i18n.svelte";
import { getThemeMode, setThemeMode, type ThemeMode } from "../theme.svelte";
import {
  getAcInfinityWerte,
  getAdGuardWerte,
  getBatterieStandard,
  getBlitzWerte,
  getMqttWerte,
  setzeAcInfinityWert,
  setzeAdGuardWert,
  pruefeAcInfinity,
  pruefeAdGuard,
  setzeBatterieStandard,
  setzeBlitzWert,
  setzeMqttWert,
  type AcInfinityWerte,
  type AdGuardWerte,
  type BlitzWerte,
  type MqttWerte,
} from "./domaenen.svelte";
import { semKey, suchText } from "./suche";
import { getSystemWerte, setzeSystemWert } from "./system.svelte";
import { getDrucker, startDrucker, verbindeUndWarte } from "../drucker-summary.svelte";
import { getClaude, setzeOhneRueckfrage } from "../claude-summary.svelte";
import { getKi, setzeKiEinstellung, startKi } from "../ki-summary.svelte";
import { dienstGeheimnisGesetzt, startDienstGeheimnisse } from "../dienst-geheimnisse-summary.svelte";
import { getWaechter, setzeEinstellung, startWaechter } from "../druckwaechter-summary.svelte";
import type { Einstellung as WaechterEinstellung } from "../drucker/waechter";

export type Bereich = "persoenlich" | "system";
//   HANDLUNG eine Sache, die man TUT, nicht einstellt: eine Verbindung
//            pruefen, einen Zwischenspeicher leeren. Sie kam dazu, als
//            die alten Register verschwinden sollten -- ihre
//            "testen"-Knoepfe sind weder Wert noch Flaeche, und ohne
//            eine dritte Sorte haetten sie das Register am Leben
//            gehalten (16.08.).
//   KARTE    eine Gruppe von Werten, die zusammen gehören und zusammen
//            bedient werden (Farbe: Vorlage, Schalter, Farbfelder). Als
//            einzelne Zeilen waeren es vierzehn; die Karte ist ein eigener
//            Baustein, den auch die zugehoerige Seite zeigt (03.10.).
export type Art = "schalter" | "zahl" | "text" | "wahl" | "flaeche" | "handlung" | "karte";

export interface Option {
  wert: string;
  label: string;
}

export interface Eintrag {
  /** Stabile ID, auch der Schluessel fuer "geaendert"-Anzeige. */
  id: string;
  name: string;
  /** Untertitel. Als Funktion, wenn er vom geladenen Zustand abhaengt:
   *  das Manifest entsteht EINMAL beim Laden, da steht noch nichts
   *  fest. Wer hier einen festen Text setzt, meint auch einen festen. */
  info?: string | (() => string);
  bereich: Bereich;
  kategorie: string;
  art: Art;
  /** Zu welcher Seite gehoert das? Treibt den Bereich "Diese Seite". */
  seite?: string;
  /** Zusaetzliche Suchbegriffe -- wer "Sprache" sucht, meint auch
   *  "Language". Analog zu den Aliassen in routes.ts. */
  such?: string[];
  /** Die ECHTEN Fragen, die man stellen wuerde ("was kostet der Strom",
   *  "wo bin ich eingeloggt") -- wie routeIntents in routes.ts. Speist
   *  den Textvergleich UND das Embedding fuer die Suche nach Bedeutung
   *  (lib/einstellungen/suche.ts). Pflichtfeld: fehlt die Zeile, faellt
   *  der Eintrag still auf Stichworte zurueck, und das merkt niemand. */
  absicht: string;

  // ── Wert-Einträge
  lies?: () => unknown;
  schreib?: (wert: unknown) => void | Promise<void>;
  standard?: unknown;
  optionen?: Option[];
  min?: number;
  max?: number;
  schritt?: number;
  /** Einheit hinter dem Feld ("Tage", "%", "€/kWh"). */
  einheit?: string;

  // ── Flaechen-Einträge
  /** Die Flaeche selbst. Die Zeile klappt im Fenster auf und zeigt sie
   *  dort -- sie fuehrt nicht mehr auf eine andere Seite (Tim 04.10.2026:
   *  "Das sollte besser bei den Einstellungen selbst abgehandelt
   *  werden"). Lazy geladen: das Manifest liegt im Start-Buendel (das
   *  Zahnrad der Tab-Leiste fragt es), die Flaechen nicht. Die Komponente
   *  bekommt embedded={true} und laesst dann Kopfzeile und Rueckweg weg.
   *  (any: Flaechen, die ohnehin nur eingebettet vorkommen, kennen das
   *  Feld nicht -- sie ignorieren es.) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inhalt?: () => Promise<{ default: Component<any> }>;
  /** Vorlage fuer eine Flaeche, die etwas anlegt (Geheimnis-Speicher): ist
   *  der Eintrag noch nicht da, oeffnet sie das Formular damit vorbelegt --
   *  der Name muss dann nicht abgetippt werden (Tim 15.10.2026, Zugangscode
   *  des Druckers: „Wie und wo hinterlegt man den PIN hier?"). */
  vorlage?: { name: string; beschreibung: string };
  /** Nur fuer Ziele, die ein eigener ORT sind und keine Einstellung
   *  (die Aufgaben): der Pfeil oeffnet die Seite als Tab. */
  ziel?: string;

  // ── Karten-Einträge
  /** Welche Karte die Zeile zeigt (Einstellungen.svelte kennt die Namen). */
  karte?: "farbe" | "push";

  // ── Handlungs-Einträge
  /** Was der Knopf tut. Die Antwort steht danach in der Zeile -- eine
   *  Pruefung ohne sichtbares Ergebnis waere nur ein Klickgeraeusch. */
  tun?: () => Promise<{ ok: boolean; text: string }>;
  /** Beschriftung des Knopfs. */
  tunLabel?: string;
}

// ─── Farbe (lib/farbe/farbe.ts, docs/gestaltung.md „Farbe") ─────────
// EINE Karte statt vieler Zeilen (Tim 03.10.2026: „geschickter und
// übersichtlicher"): Vorlage, Schalter, Akzentfarbe und die Farbe je
// Bereich stehen in lib/farbe/FarbKarte.svelte. Hier nur der Eintrag, der
// sie in die Liste holt, findbar macht und „zurücksetzen" kennt.
const farbStand = (): string => JSON.stringify({ farbe: getBgSettings().farbe, akzent: getBgSettings().accentHue });

const FARBE: Eintrag[] = [
  {
    id: "p.farbe",
    name: "Farbe",
    absicht: "Farben ändern, weniger grau und mehr bunt, Farbe je Bereich wählen, Akzentfarbe, wie werden Zustände gefärbt",
    info: "Wie viel Farbe das Homelab trägt. Grün, Gelb und Rot bleiben immer dem Zustand vorbehalten.",
    bereich: "persoenlich",
    kategorie: "Darstellung",
    art: "karte",
    karte: "farbe",
    such: [
      "farbe", "vorlage", "ruhig", "ort", "lebendig", "kräftig", "bunt", "grau", "preset",
      "akzent", "akzentfarbe", "accent", "hue", "orange", "violett", "koralle",
      "bereich", "bereichsfarbe", "symbole", "icons", "kacheln", "leiste", "seitenleiste", "baum", "tabs", "übersichten", "neutral", "schwarz",
      "diagramm", "chart", "zustand", "ok", "grün", "tönung", "fläche", "sättigung",
      "zuhause", "werkstatt", "infrastruktur", "sicherheit", "argus", "system", "daten", "claude",
      "rosenholz", "terrakotta", "sand", "oliv", "limette", "salbei", "türkis", "himmel", "petrol", "blau",
      "indigo", "violett", "orchidee", "beere", "stein", "graphit", "gesperrt",
      "vorschlag", "vorschläge", "farbsatz", "palette", "warm", "frisch", "kühl", "gedeckt",
    ],
    standard: JSON.stringify({ farbe: FARBE_STANDARD, akzent: AKZENT_STANDARD }),
    lies: farbStand,
    // Einziger Schreibweg von außen ist „zurücksetzen": alles auf Standard.
    schreib: () => setBgSettings({ farbe: { ...FARBE_STANDARD, bereiche: { ...FARBE_STANDARD.bereiche } }, accentHue: AKZENT_STANDARD }),
  },
  {
    // Die Seite bleibt eine Fläche (docs/einstellungen-inventar.md): sie
    // trägt die Vorschau, den Hintergrund, Deckkraft und Eckenradius --
    // mehr, als in Zeilen passt. Bis 03.10.2026 führte aus dem Fenster
    // kein Weg hin.
    id: "p.erscheinungsbild",
    name: "Erscheinungsbild mit Vorschau",
    absicht: "Hintergrund ändern, Karten durchsichtiger machen, Ecken runder, wie sieht das Homelab aus",
    info: "Hintergrund, Deckkraft, Eckenradius und eine Vorschau.",
    bereich: "persoenlich",
    kategorie: "Darstellung",
    art: "flaeche",
    inhalt: () => import("../../pages/SettingsTheme.svelte"),
    such: ["vorschau", "hintergrund", "deckkraft", "radius", "körnung", "theme"],
  },
];

// ─── Persoenlich (prefs -- gilt fuer dich, wandert ueber Geraete) ────

const PERSOENLICH: Eintrag[] = [
  {
    id: "p.theme",
    name: "Erscheinungsbild",
    absicht: "dunkles oder helles Design, Dark Mode einschalten, Nachtmodus",
    info: "Auto folgt dem System.",
    bereich: "persoenlich",
    kategorie: "Darstellung",
    art: "wahl",
    such: ["theme", "dark mode", "hell", "dunkel"],
    optionen: [
      { wert: "auto", label: "Auto" },
      { wert: "light", label: "Hell" },
      { wert: "dark", label: "Dunkel" },
    ],
    standard: "auto",
    lies: () => getThemeMode(),
    schreib: (v) => setThemeMode(v as ThemeMode),
  },
  ...FARBE,
  {
    id: "p.dichte",
    name: "Dichte",
    absicht: "alles kompakter oder luftiger, mehr auf den Bildschirm bekommen, Abstände kleiner machen",
    info: "Skaliert alle Abstände.",
    bereich: "persoenlich",
    kategorie: "Darstellung",
    art: "zahl",
    min: 70,
    max: 130,
    einheit: "%",
    standard: 100,
    such: ["density", "kompakt", "abstand"],
    lies: () => getBgSettings().density,
    schreib: (v) => setBgSettings({ density: Number(v) }),
  },
  {
    id: "p.kpi",
    name: "Größe der KPI-Zahlen",
    absicht: "große Zahlen kleiner oder größer machen, Schriftgröße der Kennzahlen",
    info: "Nur die großen Werte, nicht die Grundschrift.",
    bereich: "persoenlich",
    kategorie: "Darstellung",
    art: "zahl",
    min: 70,
    max: 130,
    einheit: "%",
    standard: 100,
    such: ["schrift", "gross", "zahlen"],
    lies: () => getBgSettings().largeScale,
    schreib: (v) => setBgSettings({ largeScale: Number(v) }),
  },
  {
    id: "p.bewegung",
    name: "Bewegung im Hintergrund",
    absicht: "Animation im Hintergrund abschalten, Akku sparen, ruhiger Hintergrund",
    info: "Langsame Animationen. Aus spart Energie.",
    bereich: "persoenlich",
    kategorie: "Darstellung",
    art: "schalter",
    standard: true,
    such: ["motion", "animation", "energie"],
    lies: () => getBgSettings().motion,
    schreib: (v) => setBgSettings({ motion: !!v }),
  },
  {
    id: "p.lang",
    name: "Sprache",
    absicht: "Sprache umstellen, auf Englisch oder Deutsch wechseln",
    bereich: "persoenlich",
    kategorie: "Allgemein",
    art: "wahl",
    such: ["language", "deutsch", "english"],
    optionen: [
      { wert: "de", label: "Deutsch" },
      { wert: "en", label: "English" },
    ],
    standard: "de",
    lies: () => getLang(),
    schreib: (v) => setLang(String(v)),
  },
  {
    // Kein Schalter: das adaptive Lernen ist eine Konfiguration mit
    // mehreren Reglern (Abklingen, Bonus-Deckel, Grenzen) plus dem
    // gelernten Stand zum Zuruecksetzen. Als einzelne Zeile waere davon
    // nur ein Teil sichtbar -- also eine Flaeche.
    id: "p.suche_lernen",
    name: "Adaptives Lernen der Suche",
    absicht: "warum steht das oben in der Suche, gelernte Treffer löschen, Suche soll sich Klicks merken",
    info: "Häufig geöffnete Ziele steigen im Ranking.",
    bereich: "persoenlich",
    kategorie: "Suche",
    art: "flaeche",
    inhalt: () => import("../../pages/SettingsSearch.svelte"),
    such: ["ranking", "personalisierung", "lernen"],
  },
  {
    id: "p.zugang",
    name: "Passkeys und Sitzungen",
    absicht: "wo bin ich eingeloggt, angemeldete Geräte abmelden, Passkey hinzufügen, Recovery-Codes neu erzeugen, mein Konto, Passkey fürs Recovery-Tool einrichten, Zugang zum Recovery-Tool",
    info: "Anmeldung an diesem Konto verwalten.",
    bereich: "persoenlich",
    kategorie: "Konto",
    art: "flaeche",
    inhalt: () => import("../../pages/SettingsAuth.svelte"),
    such: ["login", "passkey", "abmelden", "2fa", "recovery-tool", "network"],
  },
  {
    // Karte, keine Fläche: Push ist ein Schalter für dieses Gerät plus
    // eine kurze Geräteliste -- kein Bestand mit Anlegen und Löschen. Bis
    // 04.10.2026 verwies die Zeile auf eine eigene Seite (Tim: „sollte
    // besser bei den Einstellungen selbst abgehandelt werden").
    id: "p.push",
    name: "Benachrichtigungen",
    absicht: "Benachrichtigungen aufs Handy bekommen, Push einrichten oder testen, warum kommt kein Push, Gerät für Push entfernen",
    info: "Push auf diesem Gerät ein- und ausschalten, testen, angemeldete Geräte.",
    bereich: "persoenlich",
    kategorie: "Konto",
    art: "karte",
    karte: "push",
    such: ["push", "notification", "meldungen", "web push", "ios", "handy", "telefon", "test", "geräte"],
  },
];

// ─── System (settings -- gilt fuer die Anlage) ──────────────────────

function sys<K extends keyof NonNullable<ReturnType<typeof getSystemWerte>>>(
  schluessel: K,
) {
  return {
    lies: () => getSystemWerte()?.[schluessel],
    schreib: (v: unknown) =>
      setzeSystemWert(schluessel, v as NonNullable<ReturnType<typeof getSystemWerte>>[K]),
  };
}

/** Wie sys(), aber fuer die Blitz-Konfiguration: sie gehoert dem
 *  MQTT-Client, nicht der Settings-Tabelle (siehe domaenen.svelte.ts). */
function blitz<K extends keyof BlitzWerte>(feld: K) {
  return {
    lies: () => getBlitzWerte()?.[feld],
    schreib: (v: unknown) => setzeBlitzWert(feld, Number(v)),
  };
}

function mqttFeld<K extends keyof MqttWerte>(feld: K) {
  return {
    lies: () => getMqttWerte()?.[feld],
    schreib: (v: unknown) => setzeMqttWert(feld, v as MqttWerte[K]),
  };
}
function adguardFeld<K extends keyof AdGuardWerte>(feld: K) {
  return {
    lies: () => getAdGuardWerte()?.[feld],
    schreib: (v: unknown) => setzeAdGuardWert(feld, v as AdGuardWerte[K]),
  };
}
function aciFeld<K extends keyof AcInfinityWerte>(feld: K) {
  return {
    lies: () => getAcInfinityWerte()?.[feld],
    schreib: (v: unknown) => setzeAcInfinityWert(feld, v as AcInfinityWerte[K]),
  };
}

/** Die Kennwort-Zeile einer Verbindung. Sie zeigt NIE ein Feld -- das
 *  Kennwort liegt im Geheimnis-Speicher, hier steht nur, ob eines
 *  hinterlegt ist und wo es wohnt. Genau darum ging es bei Fall 1 im
 *  Inventar: ein Kennwortfeld hat in einer durchsuchbaren Liste
 *  nichts verloren. */
function kennwortZeile(
  id: string,
  name: string,
  gesetzt: () => boolean | undefined,
  seite: string,
  /** Wie man diese Verbindung sonst nennt ("Broker", "Werbeblocker") --
   *  die Frage "Kennwort für den Broker" traf sonst die falsche Zeile. */
  auch = "",
): Eintrag {
  return {
    id,
    name,
    info: () =>
      gesetzt()
        ? "Hinterlegt im Geheimnis-Speicher — dort ändern."
        : "Noch nicht hinterlegt.",
    bereich: "system",
    kategorie: "Integrationen",
    art: "flaeche",
    inhalt: () => import("../../pages/SettingsSecrets.svelte"),
    seite,
    such: ["passwort", "kennwort", "zugang", "secret"],
    // Der Name traegt die Verbindung ("MQTT · Kennwort") -- die Frage
    // ist bei allen dieselbe.
    absicht: `Kennwort für ${name.split(" · ")[0]} ändern oder hinterlegen, wo liegt das Passwort${auch ? ", " + auch : ""}`,
  };
}

/** Ist der RunPod-Schlüssel hinterlegt? Quelle: die Freigabeliste der
 *  Dienste (GET /v2-api/dienst/geheimnisse) -- Name und „gesetzt“, nie ein
 *  Wert. */
function runpodGesetzt(): boolean | undefined {
  startDienstGeheimnisse();
  return dienstGeheimnisGesetzt("runpod_api_key");
}

const SYSTEM: Eintrag[] = [
  {
    id: "s.timezone",
    name: "Zeitzone",
    absicht: "falsche Uhrzeit, Zeitzone einstellen, Zeitpläne laufen zur falschen Stunde",
    info: "Grundlage für Zeitpläne und Anzeigen.",
    bereich: "system",
    kategorie: "Allgemein",
    art: "text",
    standard: "Europe/Berlin",
    such: ["zeit", "timezone", "uhr"],
    ...sys("timezone"),
  },
  {
    id: "s.strompreis",
    name: "Strompreis",
    absicht: "was kostet der Strom, Preis je Kilowattstunde eintragen, Stromkosten berechnen",
    info: "Für die Umrechnung in der Energie-Auswertung.",
    bereich: "system",
    kategorie: "Allgemein",
    art: "zahl",
    einheit: "€/kWh",
    min: 0,
    // Dieselbe Grenze wie im Backend (settings.go klemmt bei 10). Eine
    // strengere Angabe hier waere eine zweite Wahrheit -- und die
    // falsche, denn der Server entscheidet.
    max: 10,
    schritt: 0.01,
    // KEIN Standard: 0 bedeutet "nicht konfiguriert", nicht
    // "Werkseinstellung". Mit standard: 0 traegt jeder echte Preis die
    // Geaendert-Marke -- ein oranger Strich ohne Aussage (Tim 15.08.).
    // Die Marke gibt es nur, wo es etwas zurueckzusetzen GIBT.
    seite: "/zuhause/energy",
    such: ["energie", "kwh", "kosten"],
    ...sys("electricity_price_eur"),
  },
  // Netzfrequenz (internal/netzfrequenz). Grenzen wie im Backend
  // (settings.go klemmt): Warnung 10-1000, Kritisch 10-2000, Haltezeit
  // 0-600. Dass Kritisch nie unter Warnung liegt, regelt der Poller.
  {
    id: "s.netzfrequenz_warn",
    name: "Netzfrequenz · Warnung ab",
    absicht: "ab wann warnt die Netzfrequenz, Schwelle für die Abweichung von 50 Hertz",
    info: "Abweichung von 50 Hz, ab der eine Warnung kommt. Ausschläge von ±20 mHz sind Alltag.",
    bereich: "system",
    kategorie: "Infrastruktur",
    art: "zahl",
    einheit: "mHz",
    min: 10,
    max: 1000,
    schritt: 10,
    standard: 100,
    seite: "/infra/netzfrequenz",
    such: ["netzfrequenz", "frequenz", "hz", "stromnetz", "schwelle"],
    ...sys("netzfrequenz_warn_mhz"),
  },
  {
    id: "s.netzfrequenz_krit",
    name: "Netzfrequenz · Kritisch ab",
    absicht: "ab wann ist die Netzfrequenz kritisch, Grenze für den Alarm",
    info: "Abweichung von 50 Hz, ab der kritisch gemeldet wird. Ab 200 mHz (49,8 Hz) aktivieren die Netzbetreiber zusätzliche Reserven.",
    bereich: "system",
    kategorie: "Infrastruktur",
    art: "zahl",
    einheit: "mHz",
    min: 10,
    max: 2000,
    schritt: 10,
    standard: 200,
    seite: "/infra/netzfrequenz",
    such: ["netzfrequenz", "frequenz", "hz", "stromnetz", "kritisch"],
    ...sys("netzfrequenz_crit_mhz"),
  },
  {
    id: "s.netzfrequenz_halt",
    name: "Netzfrequenz · Haltezeit",
    absicht: "die Netzfrequenz warnt zu oft, kurze Ausschläge übergehen, Entprellzeit",
    info: "So lange muss die Warn-Grenze überschritten sein, bevor gewarnt wird. Kritisches meldet sich nach zwei Messungen.",
    bereich: "system",
    kategorie: "Infrastruktur",
    art: "zahl",
    einheit: "s",
    min: 0,
    max: 600,
    schritt: 10,
    standard: 30,
    seite: "/infra/netzfrequenz",
    such: ["netzfrequenz", "frequenz", "haltezeit", "entprellen"],
    ...sys("netzfrequenz_hold_s"),
  },
  {
    id: "s.lat",
    name: "Standort · Breitengrad",
    absicht: "wo steht die Anlage, Wohnort eintragen, Koordinaten für Wetter und Sonnenaufgang",
    info: "Für Sonnenstand und Wetter.",
    bereich: "system",
    kategorie: "Allgemein",
    art: "zahl",
    schritt: 0.0001,
    seite: "/zuhause/wetter",
    such: ["standort", "position", "sonne"],
    ...sys("home_latitude"),
  },
  {
    id: "s.lon",
    name: "Standort · Längengrad",
    absicht: "wo steht die Anlage, Wohnort eintragen, Koordinaten für Wetter und Sonnenaufgang",
    bereich: "system",
    kategorie: "Allgemein",
    art: "zahl",
    schritt: 0.0001,
    seite: "/zuhause/wetter",
    such: ["standort", "position"],
    ...sys("home_longitude"),
  },
  {
    // Hieß bis 05.10.2026 „Aufbewahrung der Logs" und wirkte nicht: die
    // Aufräum-Aufgabe löschte erfolgreiche Läufe nach ihrem eigenen Wert
    // (14 Tage), hier und auf der Läufe-Seite standen 30. Jetzt gilt der
    // Wert für erfolgreiche Läufe.
    id: "s.log_retention",
    name: "Aufbewahrung der Läufe",
    absicht: "wie lange werden Läufe und ihre Ausgabe aufbewahrt, alte Logs löschen, Verlauf der Aufgaben kürzen, die Datenbank wird zu groß",
    info: "Erfolgreiche Läufe samt Ausgabe. Gescheiterte bleiben mindestens 90 Tage, der jüngste Lauf je Aufgabe immer.",
    bereich: "system",
    kategorie: "Aufbewahrung",
    art: "zahl",
    einheit: "Tage",
    min: 1,
    max: 3650,
    standard: 30,
    seite: "/system/runs",
    such: ["retention", "logs", "läufe", "runs", "aufraeumen"],
    ...sys("log_retention_days"),
  },
  {
    // Der Name hieß bis 05.10.2026 „Klima-Historie", und der Wert wirkte
    // gar nicht: die Aufräum-Aufgabe löschte mit ihrem eigenen festen Wert
    // (100 Tage). Jetzt gilt er für alle Messwerte außer der
    // Rechner-Auslastung (zu groß für Jahre), und der Server klemmt nicht
    // mehr auf 365.
    id: "s.klima_retention",
    name: "Aufbewahrung der Messwerte",
    absicht: "wie lange werden Messwerte der Sensoren aufbewahrt, Verlauf kürzen, Daten fünf Jahre halten, Netzfrequenz Klima Energie Verlauf aufbewahren, Historie nicht löschen",
    info: "Klima, Energie, Netzfrequenz, Geräte-Verlauf, Erreichbarkeit. 1825 = fünf Jahre. Die Rechner-Auslastung bleibt bei 100 Tagen.",
    bereich: "system",
    kategorie: "Aufbewahrung",
    art: "zahl",
    einheit: "Tage",
    min: 1,
    max: 3650,
    standard: 90,
    seite: "/zuhause/sensoren",
    such: ["retention", "sensoren", "historie", "klima", "netzfrequenz", "energie", "jahre"],
    ...sys("climate_history_retention_days"),
  },
  {
    id: "s.dns",
    name: "Primärer DNS",
    absicht: "welcher DNS-Server wird geprüft, Namensauflösung",
    bereich: "system",
    kategorie: "Netz",
    art: "text",
    seite: "/sicherheit/dns",
    such: ["dns", "adguard", "resolver"],
    ...sys("dns_primary"),
  },
  {
    id: "s.openwrt_alias",
    name: "OpenWrt-Alias",
    absicht: "wie heißt der Router, Name des Routers ändern",
    info: "Name des Routers in der Topologie.",
    bereich: "system",
    kategorie: "Netz",
    art: "text",
    seite: "/infra/network",
    such: ["router", "openwrt", "netzwerk"],
    ...sys("network_openwrt_alias"),
  },
  {
    // Seit 22.10.2026: ein Mac am zweiten Standort meldet seine Adresse,
    // SSH folgt ihr -- aber nur in diesen Netzen (internal/aufenthalt).
    // Verlangt die Vault-PIN wie der OpenWrt-Alias: es bestimmt, wohin
    // der Dienst als root verbindet.
    id: "s.ssh_netze",
    name: "SSH · Netze für gemeldete Adressen",
    absicht:
      "Mac ist am anderen Standort und SSH geht auf die falsche Adresse, welche Netze darf ein Host-Agent für SSH melden, Tailscale-Adresse erlauben",
    info: "Meldet ein Host-Agent (Mac) eine Adresse in einem dieser Netze, gehen SSH-Prüfung und SSH-Zugriffe dorthin. Durch Komma getrennt, leer = nie umleiten.",
    bereich: "system",
    kategorie: "Netz",
    art: "text",
    seite: "/infra/cluster",
    such: ["ssh", "netz", "subnetz", "cidr", "tailscale", "standort", "son", "mac", "macbook", "adresse"],
    standard: "10.0.0.0/24, 192.168.2.0/24, 100.64.0.0/10",
    lies: () => getSystemWerte()?.ssh_netze?.join(", "),
    schreib: (v: unknown) =>
      setzeSystemWert(
        "ssh_netze",
        String(v ?? "")
          .split(/[\s,;]+/)
          .filter(Boolean),
      ),
  },

  {
    id: "s.blitz_track",
    name: "Blitze · Beobachtungsradius",
    absicht: "wie weit weg werden Blitze gezeigt, Umkreis für Gewitter",
    info: "Bis hierher werden Blitze gesammelt und gezeigt.",
    bereich: "system",
    kategorie: "Wetter",
    art: "zahl",
    einheit: "km",
    min: 10,
    max: 500,
    standard: 50,
    seite: "/zuhause/wetter/blitze",
    such: ["blitz", "gewitter", "radius"],
    ...blitz("track_radius_km"),
  },
  {
    id: "s.blitz_alert",
    name: "Blitze · Warnradius",
    absicht: "ab welcher Entfernung warnt das Gewitter, Benachrichtigung bei Blitzen",
    info: "Ab hier gibt es einen Push.",
    bereich: "system",
    kategorie: "Wetter",
    art: "zahl",
    einheit: "km",
    min: 5,
    max: 200,
    standard: 20,
    seite: "/zuhause/wetter/blitze",
    such: ["blitz", "gewitter", "warnung", "push"],
    ...blitz("alert_radius_km"),
  },
  {
    id: "s.blitz_cooldown",
    name: "Blitze · Ruhe nach einer Warnung",
    absicht: "zu viele Gewitter-Meldungen, Pause zwischen zwei Blitz-Warnungen",
    info: "So lange kommt kein zweiter Push.",
    bereich: "system",
    kategorie: "Wetter",
    art: "zahl",
    einheit: "Minuten",
    min: 1,
    max: 1440,
    standard: 60,
    seite: "/zuhause/wetter/blitze",
    such: ["blitz", "cooldown", "ruhe", "push"],
    ...blitz("alert_cooldown_min"),
  },
  {
    // Die Haustür ist die eine Tür, bei der „offen" kein Normalfall ist
    // (Tim 04.10.2026). Kurz offen: gelb. Länger als diese Minuten: rot,
    // Meldung und Push (alerts.runHaustuer im Backend).
    id: "s.haustuer_min",
    name: "Haustür · Meldung, wenn sie offen steht",
    absicht: "Haustür steht offen, Wohnungstür offen gelassen, Benachrichtigung wenn die Tür zu lange offen ist, Push bei offener Tür",
    info: "Nach so vielen Minuten wird die offene Haustür rot, und es kommt eine Meldung mit Push. 0 = nie melden.",
    bereich: "system",
    kategorie: "Geräte",
    art: "zahl",
    einheit: "min",
    min: 0,
    max: 120,
    schritt: 1,
    standard: 5,
    seite: "/zuhause/sensoren",
    such: ["haustür", "wohnungstür", "tür", "door", "front", "offen", "push", "warnung"],
    ...sys("front_door_open_min"),
  },
  {
    id: "s.haustuer_sensor",
    name: "Haustür · Türkontakt",
    absicht: "welche Tür ist die Haustür, Außentür festlegen, welcher Sensor hängt an der Wohnungstür",
    info: "Name des Türkontakts an der Haustür. Nur sie wird gelb, wenn sie offen ist; alle anderen Türen dürfen offen stehen.",
    bereich: "system",
    kategorie: "Geräte",
    art: "text",
    standard: "Door Front",
    seite: "/zuhause/sensoren",
    such: ["haustür", "wohnungstür", "tür", "door", "front", "sensor", "kontakt"],
    ...sys("front_door_sensor"),
  },
  {
    id: "s.batterie_standard",
    name: "Batterie-Warnschwelle",
    absicht: "ab wie viel Prozent warnt die Batterie, Warnung bei leerem Akku",
    info: "Gilt für jedes Gerät ohne eigenen Wert.",
    bereich: "system",
    kategorie: "Geräte",
    art: "zahl",
    einheit: "%",
    min: 1,
    max: 100,
    standard: 40,
    seite: "/zuhause/geraete",
    such: ["batterie", "akku", "schwelle", "warnung"],
    lies: () => getBatterieStandard() ?? undefined,
    schreib: (v: unknown) => setzeBatterieStandard(Number(v)),
  },

  // ── Die drei Dienst-Verbindungen. Adresse und Benutzer sind Werte;
  //    das Kennwort ist es NICHT (siehe kennwortZeile).
  {
    id: "s.mqtt_enabled",
    name: "MQTT · aktiv",
    absicht: "MQTT ein- oder ausschalten, es kommen keine Sensordaten an",
    info: "Ohne das kommen keine Sensordaten an.",
    bereich: "system",
    kategorie: "Integrationen",
    art: "schalter",
    seite: "/infra/agents",
    such: ["mqtt", "broker", "mosquitto"],
    ...mqttFeld("enabled"),
  },
  {
    id: "s.mqtt_broker",
    name: "MQTT · Adresse",
    absicht: "Adresse des MQTT-Brokers, wo läuft Mosquitto",
    bereich: "system",
    kategorie: "Integrationen",
    art: "text",
    seite: "/infra/agents",
    such: ["mqtt", "broker", "mosquitto", "host"],
    ...mqttFeld("broker"),
  },
  {
    id: "s.mqtt_port",
    name: "MQTT · Port",
    absicht: "auf welchem Port läuft der MQTT-Broker",
    bereich: "system",
    kategorie: "Integrationen",
    art: "zahl",
    min: 1,
    max: 65535,
    standard: 1883,
    seite: "/infra/agents",
    such: ["mqtt", "port"],
    ...mqttFeld("port"),
  },
  {
    id: "s.mqtt_user",
    name: "MQTT · Benutzer",
    absicht: "mit welchem Benutzer meldet sich das Dashboard am MQTT-Broker an",
    bereich: "system",
    kategorie: "Integrationen",
    art: "text",
    seite: "/infra/agents",
    such: ["mqtt", "benutzer", "user"],
    ...mqttFeld("username"),
  },
  kennwortZeile(
    "s.mqtt_pw",
    "MQTT · Kennwort",
    () => getMqttWerte()?.password_set,
    "/infra/agents",
    "Kennwort für den Broker, Passwort des MQTT-Brokers, Zugangsdaten für Mosquitto",
  ),
  {
    id: "s.adguard_url",
    name: "AdGuard · Adresse",
    absicht: "Adresse von AdGuard, wo läuft der Werbeblocker",
    bereich: "system",
    kategorie: "Integrationen",
    art: "text",
    seite: "/sicherheit/dns",
    such: ["adguard", "dns", "url"],
    ...adguardFeld("url"),
  },
  {
    id: "s.adguard_user",
    name: "AdGuard · Benutzer",
    absicht: "Zugangsdaten für AdGuard, Benutzername",
    bereich: "system",
    kategorie: "Integrationen",
    art: "text",
    seite: "/sicherheit/dns",
    such: ["adguard", "dns", "benutzer"],
    ...adguardFeld("username"),
  },
  {
    id: "s.adguard_test",
    name: "AdGuard · Verbindung prüfen",
    absicht: "funktioniert die Verbindung zu AdGuard, Zugang testen",
    info: "Meldet sich mit den gespeicherten Zugangsdaten an.",
    bereich: "system",
    kategorie: "Integrationen",
    art: "handlung",
    seite: "/sicherheit/dns",
    such: ["adguard", "test", "pruefen", "verbindung"],
    tunLabel: "prüfen",
    tun: pruefeAdGuard,
  },
  kennwortZeile(
    "s.adguard_pw",
    "AdGuard · Kennwort",
    () => getAdGuardWerte()?.password_set,
    "/sicherheit/dns",
    "Zugang zum Werbeblocker, Passwort für den DNS-Filter",
  ),
  {
    id: "s.aci_email",
    name: "AC Infinity · Konto",
    absicht: "Konto für AC Infinity, Zugangsdaten der Growbox-Lüfter",
    info: "E-Mail des Herstellerkontos.",
    bereich: "system",
    kategorie: "Integrationen",
    art: "text",
    seite: "/zuhause/aktoren",
    such: ["acinfinity", "growbox", "konto", "email"],
    ...aciFeld("email"),
  },
  {
    id: "s.aci_poll",
    name: "AC Infinity · Abfrage-Abstand",
    absicht: "wie oft werden die AC-Infinity-Lüfter abgefragt",
    bereich: "system",
    kategorie: "Integrationen",
    art: "zahl",
    einheit: "Sekunden",
    min: 5,
    max: 600,
    seite: "/zuhause/aktoren",
    such: ["acinfinity", "poll", "intervall"],
    ...aciFeld("poll_interval"),
  },
  {
    id: "s.aci_test",
    name: "AC Infinity · Verbindung prüfen",
    absicht: "funktioniert die Verbindung zu AC Infinity, Zugang testen",
    info: "Meldet sich mit den gespeicherten Zugangsdaten an.",
    bereich: "system",
    kategorie: "Integrationen",
    art: "handlung",
    seite: "/zuhause/aktoren",
    such: ["acinfinity", "test", "pruefen", "verbindung"],
    tunLabel: "prüfen",
    tun: pruefeAcInfinity,
  },
  kennwortZeile(
    "s.aci_pw",
    "AC Infinity · Kennwort",
    () => getAcInfinityWerte()?.password_set,
    "/zuhause/aktoren",
  ),

  // 3D-Drucker (internal/bambu): Adresse und Seriennummer findet der Dienst
  // selbst im LAN, nur der Zugangscode muss von Hand hinein.
  {
    ...kennwortZeile(
      "s.drucker_code",
      "3D-Drucker · Zugangscode",
      () => {
        startDrucker();
        return getDrucker()?.zugangscode_gesetzt;
      },
      "/werkstatt/drucker",
      "Bambu, Access Code, LAN-Modus",
    ),
    vorlage: { name: "bambu_zugangscode", beschreibung: "Zugangscode des 3D-Druckers (LAN-Modus)" },
    info: () =>
      getDrucker()?.zugangscode_gesetzt
        ? "Hinterlegt im Geheimnis-Speicher als „bambu_zugangscode“ — dort ändern."
        : "Am Drucker unter Einstellungen › WLAN ablesen und im Geheimnis-Speicher als „bambu_zugangscode“ anlegen.",
    such: ["drucker", "bambu", "p1s", "zugangscode", "access code", "lan", "passwort"],
  },
  // Claude-API (internal/ki, docs/ki.md): das monatliche API-Guthaben,
  // getrennt vom Abo der Chats. Der Schlüssel kommt aus der Claude Console.
  {
    ...kennwortZeile(
      "s.ki_schluessel",
      "Claude-API · Schlüssel",
      () => {
        startKi();
        return getKi()?.schluessel;
      },
      "/claude",
      "Anthropic, API Key, API-Guthaben, Repo-Prüfung",
    ),
    vorlage: { name: "anthropic_api_key", beschreibung: "Schlüssel für die Claude-API (API-Guthaben, Repo-Prüfung)" },
    info: () =>
      getKi()?.schluessel
        ? "Hinterlegt im Geheimnis-Speicher als „anthropic_api_key“ — dort ändern."
        : "In der Claude Console unter API Keys anlegen und hier als „anthropic_api_key“ hinterlegen.",
    such: ["claude", "anthropic", "api", "key", "schlüssel", "guthaben", "credit", "ki"],
  },
  {
    ...kennwortZeile(
      "s.ki_admin",
      "Claude-API · Admin-Schlüssel",
      () => {
        startKi();
        return getKi()?.admin_schluessel;
      },
      "/claude",
      "Anthropic, Admin API, Kosten, Guthaben abgleichen",
    ),
    vorlage: { name: "anthropic_admin_key", beschreibung: "Admin-Schlüssel der Claude-API: nur die Kosten lesen (sk-ant-admin01-…)" },
    info: () =>
      getKi()?.admin_schluessel
        ? "Hinterlegt als „anthropic_admin_key“ — die Kosten kommen alle 15 min laut Konto."
        : "Optional, nur für Organisationen (Einzelkonten haben keine Admin-Schlüssel): Console › Settings › Admin keys. Ohne rechnet das Homelab aus dem eingetragenen Guthaben und dem eigenen Kostenbuch.",
    such: ["claude", "anthropic", "admin", "kosten", "guthaben", "abgleich", "ki"],
  },
  {
    id: "s.ki_grenze",
    name: "Claude-API · Monatslimit",
    absicht: "wie viel die Claude-API im Monat kosten darf, Ausgabengrenze, Budget, Spend Limit",
    info: "Wie das Limit der Organisation in der Console. Darüber startet das Homelab nichts.",
    bereich: "system",
    kategorie: "Integrationen",
    art: "zahl",
    min: 1,
    max: 1000,
    einheit: "USD",
    standard: 50,
    seite: "/claude",
    such: ["claude", "api", "limit", "budget", "grenze", "spend", "kosten", "ki"],
    lies: () => {
      startKi();
      return getKi()?.grenze_usd;
    },
    schreib: (v) => setzeKiEinstellung({ grenze_usd: Number(v) }),
  },
  {
    id: "s.ki_guthaben",
    name: "Claude-API · Guthaben eintragen",
    absicht: "API-Guthaben aktualisieren, nach dem Aufladen neu eintragen, Organization credits, Kontostand",
    info: () => {
      const k = getKi();
      return k?.guthaben_usd != null ? `Jetzt gerechnet: ${k.guthaben_usd.toFixed(2).replace(".", ",")} $` : "Zwei Zahlen aus der Console.";
    },
    bereich: "system",
    kategorie: "Integrationen",
    art: "flaeche",
    inhalt: () => import("../claude/KiAnker.svelte"),
    seite: "/claude",
    such: ["claude", "anthropic", "guthaben", "credits", "aufladen", "kontostand", "ki"],
  },
  // RunPod (Argus-Training, tim/argus): den Schlüssel holt Argus' Dienst auf
  // forge vor jedem Training über POST /v2-api/dienst/geheimnis (nur
  // Loopback + Token-Datei, Freigabeliste im Code -- docs/geheimnis-
  // speicher.md „Dienste auf forge“). Hier wird er nur hinterlegt.
  {
    ...kennwortZeile(
      "s.runpod_schluessel",
      "RunPod · API-Schlüssel",
      runpodGesetzt,
      "/sicherheit/geheimnisse",
      "API Key, GPU in der Cloud, Training des Kamerasystems Argus",
    ),
    vorlage: {
      name: "runpod_api_key",
      beschreibung: "RunPod-API-Schlüssel für das Argus-Training (Secure Cloud, EU; nur Pods anlegen/beenden)",
    },
    info: () =>
      runpodGesetzt()
        ? "Hinterlegt als „runpod_api_key“ — Argus liest ihn vor jedem Training auf RunPod."
        : "Bei runpod.io › Settings › API Keys einen Schlüssel anlegen (Rechte: Pods lesen/schreiben genügen; „Restricted“ wenn möglich) und hier als „runpod_api_key“ hinterlegen.",
    such: ["runpod", "gpu", "training", "argus", "api", "key", "schlüssel"],
  },
  {
    id: "s.drucker_verbinden",
    name: "3D-Drucker · Verbindung prüfen",
    absicht: "funktioniert die Verbindung zum 3D-Drucker, Drucker neu verbinden, Zugangscode testen",
    info: "Verbindet sofort neu, statt auf den nächsten Versuch zu warten.",
    bereich: "system",
    kategorie: "Integrationen",
    art: "handlung",
    seite: "/werkstatt/drucker",
    such: ["drucker", "bambu", "verbindung", "pruefen", "test"],
    tunLabel: "prüfen",
    tun: async () => {
      try {
        return await verbindeUndWarte();
      } catch (e) {
        return { ok: false, text: e instanceof Error ? e.message : String(e) };
      }
    },
  },

  // ── Flaechen: der Index kennt sie, besitzt sie aber nicht.
  {
    id: "s.hosts",
    name: "Hosts-Inventar",
    absicht: "neuen Server anlegen, Rechner hinzufügen, Reihenfolge beim Neustart",
    info: "Hosts anlegen, Rollen, Reboot-Reihenfolge.",
    bereich: "system",
    kategorie: "Infrastruktur",
    art: "flaeche",
    inhalt: () => import("../../pages/SettingsHosts.svelte"),
    seite: "/infra/cluster",
    such: ["hosts", "server", "inventar", "cluster"],
  },
  {
    id: "s.ssh",
    name: "SSH-Server",
    absicht: "SSH-Zugang für einen Rechner eintragen, wie verbindet sich das Dashboard mit einem Server",
    info: "Zugänge für Backups und Eingriffe.",
    bereich: "system",
    kategorie: "Infrastruktur",
    art: "flaeche",
    inhalt: () => import("../../pages/SettingsServers.svelte"),
    seite: "/infra/cluster",
    such: ["ssh", "zugang", "credentials"],
  },
  {
    id: "s.mqtt",
    name: "MQTT · Themen und Rechte",
    absicht: "wer darf welches MQTT-Thema lesen oder schreiben, Rechte am Broker, welche Themen darf ein Gerät senden oder empfangen, Berechtigungen der Geräte, Topics und ACL",
    info: "Wer schreibt was, wer liest was.",
    bereich: "system",
    kategorie: "Infrastruktur",
    art: "flaeche",
    inhalt: () => import("../../pages/SettingsMqtt.svelte"),
    seite: "/infra/agents",
    such: ["mqtt", "topics", "acl", "rechte"],
  },
  {
    id: "s.netifs",
    name: "Netzwerk-Interfaces",
    absicht: "Netzwerkkarte benennen, was ist eth0",
    info: "Schnittstellen benennen, die in Auswertungen auftauchen.",
    bereich: "system",
    kategorie: "Infrastruktur",
    art: "flaeche",
    inhalt: () => import("../../pages/SettingsNetworkInterfaces.svelte"),
    seite: "/infra/network",
    such: ["interface", "eth0", "netzwerk", "schnittstelle"],
  },
  {
    id: "s.storage_monitors",
    name: "Speicher-Regeln",
    absicht: "ab wann warnt der Speicher, Alarm wenn die Festplatte voll ist, Schwelle für ein Dataset",
    info: "Schwelle je Dataset.",
    bereich: "system",
    kategorie: "Infrastruktur",
    art: "flaeche",
    inhalt: () => import("../../pages/SettingsStorageMonitors.svelte"),
    seite: "/infra/storage",
    such: ["storage", "schwelle", "alarm", "zfs"],
  },
  {
    id: "s.uptime",
    name: "Erreichbarkeits-Ziele",
    absicht: "welche Webseiten werden überwacht, URL für die Erreichbarkeit hinzufügen",
    info: "URLs für den Uptime-Monitor.",
    bereich: "system",
    kategorie: "Infrastruktur",
    art: "flaeche",
    inhalt: () => import("../../pages/SettingsUptime.svelte"),
    seite: "/infra/status",
    such: ["uptime", "monitor", "erreichbarkeit"],
  },
  {
    // Tims Wahl 21.10.2026 nach 629 Freigaben in zwei Tagen: „Gar nicht
    // mehr fragen". Fragen an Tim und das Anhalten von homelab-claude
    // fragen trotzdem (internal/claude/freigaben.go).
    id: "s.claude_fragt",
    name: "Claude fragt vor Befehlen",
    absicht: "Claude soll Befehle ohne Freigabe ausführen, keine Bestätigung mehr, weniger nachfragen, Freigaben abschalten",
    info: "Aus: Befehle, Dateien und Netz ohne Rückfrage. Fragen an dich kommen weiter.",
    bereich: "system",
    kategorie: "Sicherheit",
    art: "schalter",
    standard: true,
    seite: "/claude",
    such: ["freigabe", "bestätigen", "erlauben", "permission", "rückfrage", "claude"],
    lies: () => !(getClaude()?.stand.ohne_rueckfrage ?? false),
    schreib: (v) => setzeOhneRueckfrage(!v),
  },
  {
    // Das eigene Tageslimit (Tim 01.11.2026: „am Samstag gerne noch 20 %
    // über. Damit genug Luft ist, falls was Unvorhergesehenes passiert").
    // Rechnung: lib/claude/claude.ts tageslimit, docs/claude.md.
    id: "s.claude_reserve",
    name: "Claude · Reserve am Samstag",
    absicht: "Tageslimit für das Wochenlimit des Claude-Abos, wie viel darf ich heute verbrauchen, Puffer für den letzten Tag der Woche, Verbrauch einteilen",
    info: "So viel vom Wochenlimit soll am Samstag noch frei sein. Der Rest verteilt sich gleich auf die Tage davor: das eigene Tageslimit.",
    bereich: "system",
    kategorie: "Integrationen",
    art: "zahl",
    einheit: "%",
    min: 0,
    max: 50,
    schritt: 5,
    standard: 20,
    seite: "/claude",
    such: ["claude", "tageslimit", "wochenlimit", "reserve", "puffer", "samstag", "verbrauch", "abo", "limit"],
    ...sys("claude_reserve_prozent"),
  },
  {
    id: "s.secrets",
    name: "Geheimnisse",
    absicht: "Passwörter und Zugangsdaten verwalten, Token hinterlegen, wo liegen die Geheimnisse",
    info: "Verschlüsselt; einzeln nachschlagbar freigebbar.",
    bereich: "system",
    kategorie: "Sicherheit",
    art: "flaeche",
    inhalt: () => import("../../pages/SettingsSecrets.svelte"),
    seite: "/sicherheit/geheimnisse",
    such: ["secret", "passwort", "tresor"],
  },
  {
    // Die Stellgrößen liegen im Board, je Melder ein Satz -- darum eine
    // Fläche und keine Zeilen: wie viele Melder es gibt, weiß das Manifest
    // nicht, und gelesen wird nicht aus `settings`, sondern vom Gerät.
    id: "s.funkmelder",
    name: "Funkmelder",
    absicht: "Bewegungsmelder empfindlicher oder träger stellen, Schwelle des Funkmelders ändern, wie lange hält der Melder Bewegung, LED am Melder ausschalten, Melder neu kalibrieren, Fehlalarme abstellen",
    info: "Erkennung, Schwelle, Haltezeit und LED je Melder.",
    bereich: "system",
    kategorie: "Geräte",
    art: "flaeche",
    inhalt: () => import("../../pages/SettingsFunkmelder.svelte"),
    seite: "/zuhause/funkmelder",
    such: ["funkmelder", "bewegung", "bewegungsmelder", "schwelle", "haltezeit", "led", "kalibrieren", "espectre", "csi", "empfindlichkeit"],
  },
  {
    // Boards mit eigener Firmware und Dienste im Homelab (Gateway /ws/geraet,
    // docs/protokoll.md „Phase 4"): Anlegen, Schlüssel neu ausstellen, Löschen --
    // eine Liste mit Handgriffen, also eine Fläche. Hinter der Vault-PIN.
    id: "s.protokoll-geraete",
    name: "Protokoll-Geräte und Dienste",
    absicht: "Gerät am Homelab-Protokoll anmelden, Schlüssel für ein Board mit eigener Firmware anlegen oder neu ausstellen, Feinstaub-Sensor verbinden, Gateway-Gerät löschen, ist das Board verbunden",
    info: "Boards mit eigener Firmware und Dienste: Rolle, Schlüssel, Muster und Verbindung je Eintrag.",
    bereich: "system",
    kategorie: "Geräte",
    art: "flaeche",
    inhalt: () => import("../../pages/SettingsProtokollGeraete.svelte"),
    seite: "/zuhause/geraete",
    such: ["protokoll", "gateway", "schlüssel", "schluessel", "esp32", "firmware", "feinstaub", "websocket", "homelab_protokoll", "dienst", "erkenner", "rolle"],
  },
  {
    id: "s.ha",
    name: "Home-Assistant-Instanzen",
    absicht: "Home Assistant anbinden, Adresse und Token eintragen",
    bereich: "system",
    kategorie: "Integrationen",
    art: "flaeche",
    inhalt: () => import("../../pages/SettingsHomeAssistant.svelte"),
    seite: "/zuhause/geraete",
    such: ["ha", "homeassistant"],
  },
  {
    id: "s.tasks",
    name: "Zeitpläne der Aufgaben",
    absicht: "wann laufen die Backups, Zeitplan einer Aufgabe ändern, wie lange werden Läufe aufbewahrt",
    info: "Cron je Aufgabe, Aufbewahrung der Läufe.",
    bereich: "system",
    kategorie: "Aufgaben",
    art: "flaeche",
    ziel: "/system/tasks",
    seite: "/system/tasks",
    such: ["cron", "zeitplan", "tasks"],
  },
];

// ─── Druckerwächter (internal/druckwaechter, docs/druckwaechter.md) ──
// Modus und Schwellen. Die Standardwerte sind Obicos -- das Modell ist auf
// sie abgestimmt (Tim 21.10.2026: „Schwellwerte konfigurierbar").
function waechter<K extends keyof WaechterEinstellung>(feld: K, zahl = true) {
  return {
    lies: () => {
      startWaechter();
      return getWaechter()?.einstellung?.[feld];
    },
    schreib: (v: unknown) => setzeEinstellung(feld, (zahl ? Number(v) : v) as WaechterEinstellung[K]),
  };
}

const DRUCKWAECHTER: Eintrag[] = [
  {
    id: "s.waechter_modus",
    name: "Druckerwächter",
    absicht: "Fehldruck erkennen, Spaghetti erkennen und den Druck pausieren, 3D-Druck überwachen, Kamera prüft den Druck, Wächter abschalten",
    info: "Sieht beim Drucken jede Sekunde ins Kamerabild. Bei einem Fehldruck pausieren (mit Bild als Push) oder nur melden.",
    bereich: "system",
    kategorie: "Geräte",
    art: "wahl",
    optionen: [
      { wert: "pausieren", label: "pausieren" },
      { wert: "melden", label: "nur melden" },
      { wert: "aus", label: "aus" },
    ],
    standard: "pausieren",
    seite: "/werkstatt/drucker",
    such: ["drucker", "wächter", "spaghetti", "fehldruck", "obico", "pause", "kamera", "ki"],
    ...waechter("modus", false),
  },
  {
    id: "s.waechter_empfindlichkeit",
    name: "Druckerwächter · Empfindlichkeit",
    absicht: "Wächter schlägt zu oft an, Fehlalarm beim Drucken, Wächter merkt Fehldrucke zu spät, empfindlicher machen",
    info: "Größer: früher Verdacht. 1 = wie Obico.",
    bereich: "system",
    kategorie: "Geräte",
    art: "zahl",
    min: 0.25,
    max: 4,
    schritt: 0.05,
    standard: 1,
    seite: "/werkstatt/drucker",
    such: ["drucker", "wächter", "empfindlichkeit", "sensitivity", "fehlalarm"],
    ...waechter("empfindlichkeit"),
  },
  {
    id: "s.waechter_niedrig",
    name: "Druckerwächter · Schwelle Verdacht",
    absicht: "ab wann fragt der Wächter die zweite Meinung, Schwellwert für Verdacht",
    info: "Ab diesem geglätteten Wert gilt ein Druck als verdächtig (Obico: 0,38).",
    bereich: "system",
    kategorie: "Geräte",
    art: "zahl",
    min: 0.05,
    max: 3,
    schritt: 0.01,
    standard: 0.38,
    seite: "/werkstatt/drucker",
    such: ["drucker", "wächter", "schwelle", "threshold", "verdacht"],
    ...waechter("schwelle_niedrig"),
  },
  {
    id: "s.waechter_hoch",
    name: "Druckerwächter · Schwelle sicher",
    absicht: "ab wann ist der Fehldruck sicher, Schwellwert für sicheren Fehler",
    info: "Darüber gilt der Fehler als sicher, egal wie der Druck bisher aussah (Obico: 0,78).",
    bereich: "system",
    kategorie: "Geräte",
    art: "zahl",
    min: 0.05,
    max: 5,
    schritt: 0.01,
    standard: 0.78,
    seite: "/werkstatt/drucker",
    such: ["drucker", "wächter", "schwelle", "threshold"],
    ...waechter("schwelle_hoch"),
  },
  {
    id: "s.waechter_steigerung",
    name: "Druckerwächter · Faktor bis „sicher“",
    absicht: "wie viel stärker muss das Signal sein, damit der Wächter ohne zweite Meinung pausiert",
    info: "So viel stärker als ein Verdacht muss das Signal sein, damit es als sicher gilt (Obico: 1,75).",
    bereich: "system",
    kategorie: "Geräte",
    art: "zahl",
    min: 1,
    max: 4,
    schritt: 0.05,
    standard: 1.75,
    seite: "/werkstatt/drucker",
    such: ["drucker", "wächter", "faktor", "escalating"],
    ...waechter("steigerung"),
  },
  {
    id: "s.waechter_schonzeit",
    name: "Druckerwächter · Schonzeit",
    absicht: "Wächter am Druckanfang nicht bewerten, erste Schicht nicht melden, nach Fortsetzen warten",
    info: "So lange nach Druckbeginn und nach dem Fortsetzen keine Bewertung (Obico: 5 min). 0 = keine.",
    bereich: "system",
    kategorie: "Geräte",
    art: "zahl",
    einheit: "min",
    min: 0,
    max: 60,
    standard: 5,
    seite: "/werkstatt/drucker",
    such: ["drucker", "wächter", "schonzeit", "anfang", "erste schicht"],
    // Im Dienst heißt „keine" -1 (0 ist dort „Standard").
    lies: () => {
      startWaechter();
      const v = getWaechter()?.einstellung?.schonzeit_min;
      return v === -1 ? 0 : v;
    },
    schreib: (v) => setzeEinstellung("schonzeit_min", Number(v) === 0 ? -1 : Number(v)),
  },
  {
    id: "s.waechter_meinung",
    name: "Druckerwächter · zweite Meinung",
    absicht: "bei Verdacht ein Bildmodell fragen, bevor pausiert wird, lokale KI prüft das Bild",
    info: "Bei Verdacht schaut ein Bildmodell im Homelab (Qwen3-VL) aufs Bild, bevor pausiert wird. Aus: pausiert nur, wenn der Erkenner sicher ist.",
    bereich: "system",
    kategorie: "Geräte",
    art: "schalter",
    standard: true,
    seite: "/werkstatt/drucker",
    such: ["drucker", "wächter", "zweite meinung", "vision", "qwen", "llm", "ki", "inferenz"],
    lies: () => {
      startWaechter();
      const e = getWaechter()?.einstellung;
      return e ? !e.ohne_meinung : undefined;
    },
    schreib: (v) => setzeEinstellung("ohne_meinung", !v),
  },
];

export const EINSTELLUNGEN: Eintrag[] = [...PERSOENLICH, ...SYSTEM, ...DRUCKWAECHTER];

/** Die Eintraege fuer den Embedding-Index der Suche nach Bedeutung. Wird beim
 *  Boot gefuellt (App.svelte), nicht erst beim Oeffnen des Fensters --
 *  sonst liefe die erste Frage gegen einen leeren Index. */
export function einstellungenIndex(): { key: string; text: string }[] {
  return EINSTELLUNGEN.map((e) => ({ key: semKey(e.id), text: suchText(e) }));
}

/** Passt ein Eintrag zur Suche? Sucht in Name, Beschreibung, Kategorie,
 *  den Aliassen und der Absicht -- wer "kwh" tippt, findet den
 *  Strompreis. Was dem Sinn nach passt, ohne ein Wort zu treffen,
 *  ergaenzt bge-m3 (lib/einstellungen/suche.ts). */
export function trifft(e: Eintrag, frage: string): boolean {
  const q = frage.trim().toLowerCase();
  if (!q) return true;
  const infoText = typeof e.info === "function" ? e.info() : (e.info ?? "");
  const heu = [e.name, infoText, e.kategorie, ...(e.such ?? []), e.absicht]
    .join(" ")
    .toLowerCase();
  return q.split(/\s+/).every((teil) => heu.includes(teil));
}

/** Gehoert der Eintrag zu dieser Seite? Praefix-Vergleich, damit
 *  /infra/cluster/forge auch die Cluster-Einstellungen trifft. */
export function gehoertZu(e: Eintrag, pfad: string): boolean {
  if (!e.seite || !pfad) return false;
  return pfad === e.seite || pfad.startsWith(e.seite + "/");
}

/** Gehoert der Eintrag in diesen Bereich (navSections: Zuhause, Claude …)?
 *  Seine Seite liegt unter einem Praefix des Bereichs. Treibt das Zahnrad
 *  im Kopf des Baums (EinstellungZahnrad, Tim 01.11.2026: „Ein Zahnrad
 *  reicht pro Bereich. Dort wird dann auf alle relevanten Einstellungen
 *  gefiltert"). */
export function imBereich(e: Eintrag, praefixe: readonly string[]): boolean {
  const s = e.seite;
  return !!s && praefixe.some((p) => s === p || s.startsWith(p + "/"));
}

/** Gibt es zu dieser Seite Einstellungen? Treibt das Zahnrad in der
 *  Tab-Leiste: es erscheint nur, wo es auch etwas zu zeigen gibt. */
export function hatEinstellungen(pfad: string): boolean {
  return EINSTELLUNGEN.some((e) => gehoertZu(e, pfad));
}

/** Weicht der Wert vom Standard ab? Treibt die Marke und
 *  "zurücksetzen". Ohne Standard gibt es nichts zurückzusetzen. */
export function istGeaendert(e: Eintrag): boolean {
  if (e.standard === undefined || !e.lies) return false;
  const jetzt = e.lies();
  if (jetzt === undefined || jetzt === null) return false;
  return String(jetzt) !== String(e.standard);
}
